import { useId, type FC } from 'react';
import { Plus, X } from 'lucide-react';
import type { LastPriceDto } from '@/api';
import { Button, Combobox, Input, cn } from '@/ui';

import { addDays } from '../../lib/dates';
import {
  formatDate,
  formatDecimalInput,
  formatInstantDate,
  formatMoney,
  formatNumber,
  toNumber,
} from '../../lib/format';
import {
  lineAmount,
  newLine,
  parseDecimal,
  parseInterval,
  type DraftLine,
  type ProductOption,
} from './lib/draft';

/*
 * Строки отгрузки - .pos-editor макета (PositionsEditor), плюс то, чего в макете
 * нет: цена вводится руками, а под строкой - прошлая цена клиента по этой
 * позиции из копии заказов 1С с кнопкой «Подставить». Прайс УТ не ведётся, так
 * что прошлая цена - лучшая подсказка (plan/shipment-onec-order, разведка).
 *
 * Там же - свободный остаток товара на складе отправки (plan/available-stock):
 * заказ в 1С его резервирует, и не хватит - передать отгрузку не дадут. Строки с
 * одним товаром складываются, как при проверке на сервере.
 *
 * И интервал до следующей отгрузки товара (plan/shipment-plan-from-history): по
 * нему план из истории продолжает цепочку. Хранится интервалом, а рядом
 * показана дата - она сдвигается вместе с плановой датой отгрузки.
 */

export interface PositionsEditorProps {
  lines: ReadonlyArray<DraftLine>;
  onChange: (lines: ReadonlyArray<DraftLine>) => void;
  productOptions: ReadonlyArray<ProductOption>;
  isProductsLoading: boolean;
  onProductQueryChange: (query: string) => void;
  /** Прошлая цена клиента по идентификатору продукта. */
  lastPrices: ReadonlyMap<string, LastPriceDto>;
  /**
   * Свободно на складе отправки по идентификатору продукта; null - остаток
   * неизвестен. Продукта нет в карте - склад не выбран или ответа ещё нет.
   */
  availableStock: ReadonlyMap<string, number | null>;
  /** Плановая дата отгрузки: от неё считается дата следующей. */
  plannedDate: string;
}

const GRID =
  'grid grid-cols-[minmax(0,1fr)_104px_112px_104px_32px] items-center gap-2';

const productLabel = (product: ProductOption): string =>
  `${product.code} · ${product.name}`;

const positionsWord = (count: number): string => {
  if (count % 10 === 1 && count % 100 !== 11) return 'позиция';
  if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100))
    return 'позиции';
  return 'позиций';
};

export const PositionsEditor: FC<PositionsEditorProps> = ({
  lines,
  onChange,
  productOptions,
  isProductsLoading,
  onProductQueryChange,
  lastPrices,
  availableStock,
  plannedDate,
}) => {
  const intervalFieldId = useId();
  const update = (key: string, patch: Partial<DraftLine>) =>
    onChange(
      lines.map((line) => (line.key === key ? { ...line, ...patch } : line)),
    );

  // Сколько нужно по товару во всех строках: заказ резервирует их вместе.
  const requiredByProduct = new Map<string, number>();
  for (const line of lines) {
    if (line.product === null) continue;
    const quantity = parseDecimal(line.quantity) ?? 0;
    requiredByProduct.set(
      line.product.id,
      (requiredByProduct.get(line.product.id) ?? 0) + quantity,
    );
  }

  const total = lines.reduce((sum, line) => sum + (lineAmount(line) ?? 0), 0);
  const hasUnpriced = lines.some(
    (line) => line.product !== null && parseDecimal(line.price) === null,
  );

  return (
    <div className="rounded-lg border border-ink-200">
      <div
        className={`${GRID} rounded-t-lg border-b border-ink-150 bg-ink-50 px-2.5 py-2 text-[11px] tracking-[0.05em] text-ink-500 uppercase`}
      >
        <span>Товар</span>
        <span>Кол-во</span>
        <span>Цена без НДС</span>
        <span className="text-right">Сумма</span>
        <span />
      </div>

      {lines.map((line, index) => {
        const amount = lineAmount(line);
        const hint =
          line.product === null ? undefined : lastPrices.get(line.product.id);
        const hintPrice = hint === undefined ? undefined : toNumber(hint.price);
        const isHintApplied =
          hintPrice !== undefined && parseDecimal(line.price) === hintPrice;
        const available =
          line.product === null
            ? undefined
            : availableStock.get(line.product.id);
        const required =
          line.product === null
            ? 0
            : (requiredByProduct.get(line.product.id) ?? 0);
        const isShort =
          available !== undefined && available !== null && required > available;
        const interval = parseInterval(line.interval);
        const nextDate =
          interval === null || Number.isNaN(interval) || plannedDate === ''
            ? null
            : addDays(plannedDate, interval);

        return (
          <div key={line.key} className="border-b border-ink-150 px-2.5 py-2">
            <div className={GRID}>
              <Combobox
                aria-label={`Товар, позиция ${index + 1}`}
                value={line.product}
                onChange={(product) => update(line.key, { product })}
                onQueryChange={onProductQueryChange}
                options={productOptions}
                isLoading={isProductsLoading}
                getKey={(product) => product.id}
                getLabel={productLabel}
                placeholder="Найти по артикулу или названию"
              />
              <QuantityInput
                index={index}
                value={line.quantity}
                unitName={line.product?.unitName}
                onChange={(quantity) => update(line.key, { quantity })}
              />
              <Input
                aria-label={`Цена без НДС, позиция ${index + 1}`}
                inputMode="decimal"
                placeholder="не указана"
                value={line.price}
                onChange={(event) =>
                  update(line.key, { price: event.target.value })
                }
                className="h-8 text-[12.5px]"
              />
              <span className="text-right text-[12.5px] whitespace-nowrap text-ink-700 tabular-nums">
                {amount === null ? '-' : formatMoney(amount)}
              </span>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Удалить позицию ${index + 1}`}
                title="Удалить позицию"
                disabled={lines.length === 1}
                onClick={() =>
                  onChange(lines.filter((item) => item.key !== line.key))
                }
                className="px-0"
              >
                <X aria-hidden className="size-3.5" />
              </Button>
            </div>

            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-ink-500">
              <label htmlFor={`${intervalFieldId}-${line.key}`}>
                Следующая отгрузка через
              </label>
              <Input
                id={`${intervalFieldId}-${line.key}`}
                inputMode="numeric"
                placeholder="-"
                value={line.interval}
                onChange={(event) =>
                  update(line.key, { interval: event.target.value })
                }
                className="h-7 w-16 px-2 text-[12px]"
              />
              <span>дн.</span>
              {nextDate !== null && (
                <span className="text-ink-700">· {formatDate(nextDate)}</span>
              )}
            </div>

            {available === null && (
              <div className="mt-1.5 text-xs text-st-approval">
                Свободный остаток неизвестен: 1С его ещё не прислала
              </div>
            )}
            {available !== undefined && available !== null && (
              <div
                className={`mt-1.5 text-xs ${isShort ? 'font-medium text-st-delayed' : 'text-ink-500'}`}
              >
                Свободно на складе:{' '}
                <b className="tabular-nums">{formatNumber(available)}</b>
                {isShort &&
                  ` · не хватает ${formatNumber(required - available)}`}
              </div>
            )}
            {hint !== undefined && hintPrice !== undefined && (
              <div className="mt-1.5 text-xs text-ink-500">
                Прошлая цена клиента:{' '}
                <b className="text-ink-800 tabular-nums">
                  {formatMoney(hintPrice)}
                </b>{' '}
                · заказ {hint.orderNumber} от{' '}
                {formatInstantDate(hint.orderDate)}
                {!isHintApplied && (
                  <button
                    type="button"
                    onClick={() =>
                      update(line.key, { price: formatDecimalInput(hintPrice) })
                    }
                    className="ml-2 cursor-pointer font-medium text-brand-green-700 hover:underline"
                  >
                    Подставить
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}

      <div className="flex flex-wrap items-center gap-3 rounded-b-lg bg-ink-50 px-2.5 py-2.5">
        <Button size="sm" onClick={() => onChange([...lines, newLine()])}>
          <Plus aria-hidden /> Добавить позицию
        </Button>
        <div className="ml-auto flex items-center gap-2.5 text-[12.5px] text-ink-600">
          <span>
            {lines.length} {positionsWord(lines.length)}
            {hasUnpriced && ' · есть без цены'}
          </span>
          <b className="text-sm text-ink-900 tabular-nums">
            {formatMoney(total)}
          </b>
        </div>
      </div>
    </div>
  );
};

/** Количество строки с единицей хранения товара подписью справа, как в 1С. */
const QuantityInput: FC<{
  index: number;
  value: string;
  unitName?: string | null;
  onChange: (value: string) => void;
}> = ({ index, value, unitName, onChange }) => {
  const unit = unitName?.trim();

  return (
    <div className="relative">
      <Input
        aria-label={`Количество${unit ? `, ${unit}` : ''}, позиция ${index + 1}`}
        inputMode="decimal"
        placeholder="0"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={cn('h-8 text-[12.5px]', unit && 'pr-11')}
      />
      {unit && (
        <span
          aria-hidden
          className="pointer-events-none absolute top-1/2 right-2 -translate-y-1/2 text-[11.5px] text-ink-500"
        >
          {unit}
        </span>
      )}
    </div>
  );
};
